import { apiClient } from './client';
import { narrowAttachments } from '../lib/attachments';
import type { MessageAttachment } from '../types/api';

/**
 * Mail API 클라이언트 (Phase 3, P3-MAIL-01) — read-only 골격.
 *
 * backend `apps/chat-server/src/modules/mail/mail.routes.ts` 와 1:1.
 *
 * 본 사이클은 listMessages 만 — 메일 작성·답장·폴더 이동·별표 토글·읽음 표시는 후속 atomic.
 *
 * yaml SSOT 정합은 Stage 3 패턴으로 후속 사이클에 통합 — 본 사이클은 manual type.
 */

/**
 * 메일함. 'all'(전체메일)·'self'(내게쓴메일함)는 2026-08-21 에 붙었고 **표도 칸도 만들지 않는다** —
 * 'self' = 보낸 사람도 받는 사람도 나 · 'all' = 받은 ∪ 보낸.
 * ★ 옛 서버는 이 둘을 모르고 400 을 낸다 → 화면이 **셈 API 로 먼저 물어보고** 될 때만 보여 준다.
 */
export type MailFolder = 'inbox' | 'sent' | 'draft' | 'star' | 'trash' | 'spam' | 'all' | 'self' | 'my';

export interface MailListItem {
  id: string;
  subject: string;
  bodyPreview: string;
  bodyType: string;
  sentAt: string | null;
  isDraft: boolean;
  isRead: boolean;
  isStarred: boolean;
  isTrash: boolean;
  /** 스팸 (migration 111). 구 서버는 안 준다. */
  isSpam?: boolean;
  sender: MailSenderView;
  recipientCount: number;
  /** 몇 명이 읽었나 — **내가 보낸 메일에만** 붙는다(수신확인, 2026-08-21). 구 서버는 안 준다. */
  readCount?: number;
  /**
   * 첨부 개수 (2026-08-22) — 목록 줄의 클립 배지. 없으면 서버가 아예 안 보낸다.
   * ★ 구 서버는 이 칸을 모른다 → undefined → 배지를 안 그린다(무회귀).
   */
  attachmentCount?: number;
  /**
   * **내가** 어떤 자격으로 받았나 — 'TO' | 'CC' | 'BCC' (2026-08-22).
   * 참조로 받은 줄에 표식을 붙이려는 것이라, 내가 보낸 메일에는 안 온다.
   */
  myType?: string;
  /**
   * 이 줄에 붙은 태그 (migration 161). **내 수신자 줄**에 붙은 것이라 남의 태그는 안 온다.
   * ★ 구 서버는 이 칸을 모른다 → undefined → 칩을 안 그린다(무회귀).
   */
  tags?: Array<{ id: string; name: string; color: MailTagColor }>;
}

/**
 * 보낸 사람 (migration 112).
 *
 * 사내 메일은 지금까지와 같다. **외부에서 가져온 메일**만 `isExternal: true` 가 붙고, 그때
 * `id` 는 빈 문자열이다 — 우리 사용자 id 공간에 없는 사람이라 있는 척하면 안 된다
 * (그 id 로 답장을 만들면 서버가 "그런 수신자 없음" 으로 거절한다).
 * 구 서버는 이 칸 자체를 안 준다 — 그때는 전부 사내 메일이다.
 */
export interface MailSenderView {
  id: string;
  name: string;
  email: string;
  isExternal?: boolean;
}

export interface MailListResponse {
  messages: MailListItem[];
  nextCursor: string | null;
  folder: MailFolder;
  /** 총 통수 — `withTotal` 을 달라고 했고 서버가 새것일 때만 온다(2026-08-21). */
  total?: number;
}

/** 목록 정렬 (2026-08-20). 서버가 안 받으면 안 보낸 것과 같은 결과가 나온다. */
export type MailSort = 'date' | 'sender' | 'subject' | 'unread';
export type MailOrder = 'asc' | 'desc';

export interface MailListParams {
  cursor?: string;
  /**
   * 보낸편지함에서 **치운 것만** 볼까 (migration 115, 2026-08-23).
   *
   * 보낸 메일을 치우면 보낸편지함에서 빠진다. 되돌리려면 그것을 볼 자리가 있어야 하는데,
   * 휴지통은 받는 사람 행 기반이라 보낸 메일이 낄 자리가 없다 — 보낸편지함의 토글로 본다.
   * ★ 구 서버는 이 파라미터를 모른다(400) → 아래 listMail 이 빼고 다시 부른다.
   */
  trashed?: boolean;
  limit?: number;
  sort?: MailSort;
  order?: MailOrder;
  /** 찾을 낱말 (2026-08-20). 안 주면 지금까지와 같다. */
  q?: string;
  /** 거르기 (2026-08-21). 안 주면 지금까지와 같다. */
  unread?: boolean;
  star?: boolean;
  attach?: boolean;
  to?: boolean;
  /** 총 통수를 함께 달라고 한다 — 쪽 번호용. */
  withTotal?: boolean;
  /** 내가 만든 메일함 (migration 114) — folder='my' 일 때 어느 폴더인가. */
  folderId?: string;
}

export async function listMail(folder: MailFolder, params?: MailListParams): Promise<MailListResponse> {
  const shape = (d: MailListResponse): MailListResponse =>
    // 방어 가드(additive) — messages 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
    ({ ...d, messages: Array.isArray(d?.messages) ? d.messages : [] });

  try {
    const { data } = await apiClient.get<MailListResponse>('/mail/messages', {
      params: { folder, ...params },
    });
    return shape(data);
  } catch (err: unknown) {
    // ★ 배포 순서 결합 제거 — 정렬을 모르는 옛 서버는 질의 스키마가 닫혀 있어 400 을 낸다.
    //   그때 정렬만 빼고 한 번 더 부른다. 그러면 서버·화면 배포 순서를 맞출 필요가 없다.
    //   (같은 저장소의 presign 필드 추가 때 쓴 방법과 같다.)
    const status = (err as { response?: { status?: number } }).response?.status;
    // 정렬·검색 둘 다 옛 서버가 모르는 칸이다 — 하나라도 보냈으면 빼고 다시 부른다.
    const hadNew = params?.sort !== undefined || params?.order !== undefined || params?.q !== undefined
      || params?.unread !== undefined || params?.star !== undefined
      || params?.attach !== undefined || params?.to !== undefined || params?.folderId !== undefined
      || params?.withTotal !== undefined || params?.trashed !== undefined;
    if (status !== 400 || !hadNew) throw err;
    const {
      sort: _s, order: _o, q: _q,
      unread: _u, star: _st, attach: _a, to: _t, withTotal: _w, folderId: _f, trashed: _tr,
      ...rest
    } = params ?? {};
    const { data } = await apiClient.get<MailListResponse>('/mail/messages', {
      params: { folder, ...rest },
    });
    return shape(data);
  }
}

export interface MailRecipientDetail {
  id: string;
  /**
   * 수신확인 (2026-08-21) — **내가 보낸 메일을 볼 때만** 온다. 구 서버는 안 준다.
   * 밖으로 보낸 주소에는 아예 안 붙는다 — 우리 화면 밖에서 열리므로 알 길이 없다.
   */
  isRead?: boolean;
  readAt?: string | null;
  /** 밖의 주소면 빈 문자열 (migration 113). */
  userId: string;
  type: string;
  name: string;
  email: string;
  /** 밖의 주소 (migration 113). 구 서버는 안 준다. */
  isExternal?: boolean;
}

/**
 * 보낸 사람으로서의 상태 (migration 115, 2026-08-23).
 *
 * 별표·읽음·휴지통은 전부 **받는 사람 행**에 붙어 있어, 내가 보낸 메일에는 `myStatus` 가 없다.
 * 그래서 화면이 도구를 통째로 안 그렸고 "삭제가 안 된다" 가 됐다. 보낸 사람에게 뜻이 있는
 * 것은 **치웠나 아닌가** 하나뿐이라 그것만 온다. 구 서버는 이 칸 자체를 안 준다.
 */
export interface MailSenderStatus {
  isTrash: boolean;
  /** 초안이면 치우기가 **되돌릴 수 없는 삭제**다 — 문구가 달라진다. */
  isDraft: boolean;
}

export interface MailMyStatus {
  isRead: boolean;
  isStarred: boolean;
  isTrash: boolean;
  /** 스팸 (migration 111). 구 서버는 안 준다 — 그때는 없는 것으로 본다. */
  isSpam?: boolean;
}

export interface MailDetail {
  id: string;
  subject: string;
  body: string;
  bodyType: string;
  sentAt: string | null;
  isDraft: boolean;
  /**
   * 메일 첨부 — backend `mail.routes.ts` compose 시 sender ownership + verified 강제.
   * yaml SSOT (`MailDetailResponse.attachments`) 가 typed `MessageAttachment[]` 로 정밀화 (2026-04-29).
   * narrow cast 는 getMail() 에서 적용 — wire response 가 unknown 이면 빈 배열 fallback.
   */
  attachments: MessageAttachment[];
  createdAt: string;
  sender: MailSenderView;
  recipients: MailRecipientDetail[];
  /** sender 측은 null. recipient 측은 본인 status. */
  myStatus: MailMyStatus | null;
  /** 내가 보낸 메일일 때만 온다 (migration 115). 구 서버는 안 준다 → 지금까지와 같다. */
  senderStatus?: MailSenderStatus | null;
  /**
   * 어느 외부 계정으로 받은 메일인가 (2026-09-07). 사내 메일이면 null.
   * ★ 답장을 «받은 그 계정»으로 보내려고 화면이 이 값을 이어 붙인다 — 없으면 기본 계정에서
   *   나가서, 받는 쪽은 자기가 쓴 적 없는 주소로 답을 받는다. 구 서버는 안 준다(undefined).
   */
  externalAccountId?: string | null;
}

interface MailDetailRaw extends Omit<MailDetail, 'attachments'> {
  attachments: unknown;
}

export async function getMail(id: string): Promise<MailDetail> {
  const { data } = await apiClient.get<MailDetailRaw>(`/mail/messages/${id}`);
  return {
    ...data,
    attachments: narrowAttachments(data.attachments),
  };
}

export interface PatchMailInput {
  /** 메일함 옮기기 (migration 114). null 이면 받은메일함으로 되돌린다. */
  folderId?: string | null;
  isStarred?: boolean;
  isTrash?: boolean;
  isRead?: boolean;
  isSpam?: boolean;
  /** "앞으로 이 사람 메일도" — 한 통을 치우는 것과 다른 일이라 따로 보낸다. */
  blockSender?: boolean;
}

export interface PatchMailResponse {
  id: string;
  /** 내가 보낸 메일이면 null 이 온다 (migration 115) — 그때는 senderStatus 를 본다. */
  myStatus: MailMyStatus | null;
  senderStatus?: MailSenderStatus | null;
}

export async function patchMail(id: string, input: PatchMailInput): Promise<PatchMailResponse> {
  const { data } = await apiClient.patch<PatchMailResponse>(`/mail/messages/${id}`, input);
  return data;
}

export interface BulkPatchMailResult {
  /** 실제로 바뀐 통 수 — 요청 수보다 적을 수 있다(남이 받은 메일·아직 안 보낸 것). */
  updated: number;
  /** 실제로 바뀐 메일 id. 화면이 이 목록만 갱신한다. */
  ids: string[];
}

/**
 * 여러 통을 한 번에 (2026-08-20).
 *
 * ★ 이 길을 모르는 서버(404·405)에서는 **한 통씩 부르는 방식으로 되돌아간다** — 그래서 서버와
 *   화면의 배포 순서를 맞출 필요가 없다. 되돌아간 경우에도 실패한 통은 빼고 성공한 것만 돌려준다.
 */
export async function bulkPatchMail(ids: string[], input: PatchMailInput): Promise<BulkPatchMailResult> {
  if (ids.length === 0) return { updated: 0, ids: [] };
  try {
    const { data } = await apiClient.patch<BulkPatchMailResult>('/mail/messages', { ids, ...input });
    return { updated: data?.updated ?? 0, ids: Array.isArray(data?.ids) ? data.ids : [] };
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status !== 404 && status !== 405) throw err;
    // 한 통씩 — 한 통이 실패해도 나머지는 계속한다(전부 실패하면 빈 결과가 나간다).
    const done: string[] = [];
    for (const id of ids) {
      try { await patchMail(id, input); done.push(id); } catch { /* 이 통만 건너뛴다 */ }
    }
    return { updated: done.length, ids: done };
  }
}

export interface ComposeMailInput {
  subject: string;
  body: string;
  bodyType?: 'HTML' | 'TEXT' | 'MARKDOWN';
  isDraft?: boolean;
  recipientUserIds: { to: string[]; cc?: string[]; bcc?: string[] };
  /**
   * 밖의 주소 (migration 113). 없으면 지금까지와 같은 사내 전용 발송이고, 옛 서버는
   * 이 칸을 몰라 400 을 낸다 — 아래 composeMail 이 빼고 한 번 더 부른다.
   */
  externalRecipients?: { to?: string[]; cc?: string[]; bcc?: string[] };
  /**
   * 어느 계정으로 보낼 것인가 (2026-09-07). 없으면 기본 발신 계정 — 지금까지와 같다.
   * ★ **보낼 수 있는 계정이 둘 이상일 때만** 담는다. 하나뿐이면 고를 것이 없고, 이 칸을
   *   모르는 옛 서버에 400 을 맞을 이유도 없다.
   */
  sendingAccountId?: string;
  /**
   * 메일 첨부 — yaml SSOT 의 MessageAttachment[] 와 정합 (chat send 와 동일 shape).
   * backend mail.routes.ts compose 가 발신자 ownership + verified + storagePath 강제.
   * 빈 배열 또는 undefined → backend 가 [] 로 처리.
   */
  attachments?: MessageAttachment[];
}

export interface ComposeMailResponse {
  id: string;
  isDraft: boolean;
  sentAt: string | null;
  recipientCount: number;
}

export async function composeMail(input: ComposeMailInput): Promise<ComposeMailResponse> {
  try {
    const { data } = await apiClient.post<ComposeMailResponse>('/mail/messages', input);
    return data;
  } catch (err: unknown) {
    // ★ 배포 순서 결합 제거 — 이 칸을 모르는 옛 서버는 본문 스키마가 닫혀 있어 400 을 낸다.
    //   **밖의 주소가 실제로 없을 때만** 빼고 다시 부른다. 주소가 있는데 조용히 빼면
    //   "보냈는데 안 갔다" 가 되므로 그때는 그대로 올린다.
    const status = (err as { response?: { status?: number } }).response?.status;
    const ext = input.externalRecipients;
    const hasAddress = ((ext?.to?.length ?? 0) + (ext?.cc?.length ?? 0) + (ext?.bcc?.length ?? 0)) > 0;
    if (status !== 400 || ext === undefined || hasAddress) throw err;
    const { externalRecipients: _drop, ...rest } = input;
    const { data } = await apiClient.post<ComposeMailResponse>('/mail/messages', rest);
    return data;
  }
}


/**
 * 요약 4칸의 숫자 (2026-08-21).
 *
 * ★ 넷을 **한 번의 호출**로 받는다. 메일함마다 숫자를 달지 않기로 한 덕에 화면당 한 번이면 된다.
 * ★ 뜻이 없는 칸은 null 이다(보낸메일함의 "안읽음" 등) — 0 을 주면 "다 읽었다" 로 읽힌다.
 * ★ **이 길이 없으면(구 서버 404) null 을 준다.** 그때 화면은 받아 온 것만 세고 `+` 를 붙인다.
 *   이 한 번의 물음이 "서버가 새것인가" 의 대답도 겸한다 — 전체메일·내게쓴메일함·거르기·총 통수가
 *   모두 같은 판에 들어왔기 때문이다.
 */
export interface MailCounts {
  folder: MailFolder;
  total: number;
  unread: number | null;
  star: number | null;
  attach: number;
  to: number | null;
}

export async function getMailCounts(folder: MailFolder, folderId?: string): Promise<MailCounts | null> {
  try {
    const { data } = await apiClient.get<MailCounts>('/mail/counts', {
      params: { folder, ...(folderId ? { folderId } : {}) },
    });
    return data ?? null;
  } catch (err: unknown) {
    // ★ 배포 순서 결합 제거 — folderId 를 모르는 옛 서버는 질의 스키마가 닫혀 있어 400 을 낸다.
    //   그때 folderId 만 빼고 한 번 더 부른다. 숫자는 덜 정확해도, 이 물음이 실패하면
    //   화면이 "서버가 옛것" 으로 보고 **내 메일함 나무를 통째로 감춘다** — 그게 더 나쁘다.
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status === 400 && folderId) {
      try {
        const { data } = await apiClient.get<MailCounts>('/mail/counts', { params: { folder } });
        return data ?? null;
      } catch { return null; }
    }
    // 길이 없거나(404) 막혀 있으면 조용히 물러난다 — 숫자가 없다고 화면이 멈추면 안 된다.
    return null;
  }
}


/* ═══════════════════════════════════════════════════════════════════
   내가 만드는 메일함 (migration 114, 2026-08-21)
   ═══════════════════════════════════════════════════════════════════ */

export interface MailFolderNode {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
  unread: number;
  /**
   * 보관기간(일) — **null 이 「무제한」**이다 (migration 159, 2026-09-05).
   * ★ 옛 서버는 이 칸을 아예 안 준다(undefined) → 화면이 「무제한」으로 읽는다.
   */
  retentionDays?: number | null;
}

/**
 * 내 메일함 전부(평평한 목록 + parentId). 나무로 접는 것은 화면이 한다.
 * ★ 길이 없으면(구 서버) **빈 배열**을 준다 — 폴더가 없는 것과 같은 화면이 되어,
 *   서버·화면 배포 순서를 맞출 필요가 없다.
 */
export async function listMailFolders(): Promise<MailFolderNode[]> {
  try {
    const { data } = await apiClient.get<{ folders: MailFolderNode[] }>('/mail/folders');
    return Array.isArray(data?.folders) ? data.folders : [];
  } catch {
    return [];
  }
}

export async function createMailFolder(name: string, parentId: string | null): Promise<MailFolderNode> {
  const { data } = await apiClient.post<MailFolderNode>('/mail/folders', { name, parentId });
  return data;
}

export async function updateMailFolder(
  id: string, input: { name?: string; parentId?: string | null; retentionDays?: number | null },
): Promise<MailFolderNode> {
  const { data } = await apiClient.patch<MailFolderNode>(`/mail/folders/${id}`, input);
  return data;
}

export async function deleteMailFolder(id: string): Promise<{ id: string; children: number }> {
  const { data } = await apiClient.delete<{ id: string; children: number }>(`/mail/folders/${id}`);
  return data;
}

/** 이 메일함을 통째로 읽음 처리. 조건을 보내고 **서버가 고른다**. */
export async function markFolderRead(folder: MailFolder, folderId?: string): Promise<{ updated: number }> {
  const { data } = await apiClient.post<{ updated: number }>('/mail/mark-read', { folder, ...(folderId ? { folderId } : {}) });
  return data;
}

/**
 * 같은 보낸 사람의 메일을 한 번에 옮긴다 (2026-11-02). 조건을 보내고 **서버가 고른다** —
 * 지금 쪽에 안 보이는 같은 사람 메일까지 옮긴다. 지금 보고 있는 메일함 안의 것만.
 * `targetFolderId` 가 null 이면 받은편지함으로 되돌린다.
 */
export async function moveMailBySender(
  folder: MailFolder, folderId: string | undefined, senderEmail: string, targetFolderId: string | null,
): Promise<{ moved: number }> {
  const { data } = await apiClient.post<{ moved: number }>('/mail/move-by-sender', {
    folder, ...(folderId ? { folderId } : {}), senderEmail, targetFolderId,
  });
  return data;
}

/**
 * 이 메일함 비우기.
 * ★ 휴지통·스팸함이면 **영원히 지운다**(`permanent: true`). 나머지는 휴지통으로 옮긴다.
 */
/**
 * 고른 것만 영원히 지우기 (2026-08-25) — 휴지통·스팸함 전용.
 *
 * 서버가 **치워 둔 것인지 다시 확인한다**(받은편지함 메일은 안 지워진다).
 * ⚠ 길이 없는 서버(구 버전)에서는 404 가 온다 — 부르는 쪽이 그 사실을 알아야 하므로
 *   조용히 성공한 척하지 않는다. 화면은 그때 단추를 아예 안 보여 준다(`serverNew`).
 */
export async function purgeMail(ids: string[]): Promise<{ deleted: number; ids: string[] }> {
  const { data } = await apiClient.post<{ deleted: number; ids: string[] }>(
    '/mail/messages/purge', { ids },
  );
  return { deleted: data?.deleted ?? 0, ids: Array.isArray(data?.ids) ? data.ids : [] };
}

export async function emptyFolder(
  folder: MailFolder, folderId?: string,
): Promise<{ deleted: number; moved: number; permanent: boolean }> {
  const { data } = await apiClient.post<{ deleted: number; moved: number; permanent: boolean }>(
    '/mail/empty', { folder, ...(folderId ? { folderId } : {}) },
  );
  return data;
}

/**
 * 내가 주고받은 주소 (2026-08-23).
 *
 * 수신자 자동완성은 여태 **사내 사용자 명부만** 뒤졌다. 밖의 주소는 한 번 보냈던 곳이라도
 * 다시 칠 때 아무 도움을 못 받았다. 받은편지함·보낸편지함에 실제로 찍혀 있는 주소를
 * **많이 쓴 순 → 최근 순**으로 준다.
 *
 * ★ 길이 없는 서버(구 버전)에서는 **빈 배열**을 준다 — 자동완성에 사내 사용자만 뜨는
 *   지금까지의 화면이 되어, 서버·화면 배포 순서를 맞출 필요가 없다.
 */
export interface MailAddress {
  email: string;
  /** 가장 최근에 쓰인 이름. 이름 없이 주소만 오간 곳은 null. */
  name: string | null;
  uses: number;
  lastAt: string | null;
}

export async function listMailAddresses(
  q: string,
  limit = 8,
  signal?: AbortSignal,
): Promise<MailAddress[]> {
  try {
    const { data } = await apiClient.get<{ addresses: MailAddress[] }>('/mail/addresses', {
      params: { ...(q ? { q } : {}), limit },
      signal,
    });
    return Array.isArray(data?.addresses) ? data.addresses : [];
  } catch (err) {
    // 끊긴 요청은 그대로 올려 보낸다 — 부르는 쪽이 조용히 넘긴다.
    if (err instanceof Error && err.name === 'CanceledError') throw err;
    return [];
  }
}

/* ══════════════ 환경설정 > 메일함 (migration 159, 2026-09-05) ══════════════ */

/** 메일함 하나의 셈 — 「안읽은」이 `null` 인 것은 **뜻이 없다**는 말이다(보낸편지함·임시보관함). */
export interface MailboxStat {
  unread: number | null;
  total: number;
  /** 첨부 바이트 합. 워크스페이스 용량과 같은 잣대다. */
  sizeBytes: number;
}

/** 시스템 메일함 키 — 표에 서는 것들. */
export type MailSystemKey = 'inbox' | 'star' | 'sent' | 'draft' | 'self' | 'spam' | 'trash';

export interface MailboxStats {
  system: Record<MailSystemKey, MailboxStat>;
  custom: Array<{ id: string } & MailboxStat>;
}

/**
 * 메일함마다 안읽은/전체/사용량.
 *
 * ★ 옛 서버에는 이 길이 없다(404) → **null** 을 준다. 표는 「—」로 그린다 —
 *   0 으로 주면 「메일이 하나도 없다」는 거짓이 된다.
 */
export async function getMailboxStats(): Promise<MailboxStats | null> {
  try {
    const { data } = await apiClient.get<MailboxStats>('/mail/mailbox-stats');
    return data && typeof data === 'object' && data.system ? data : null;
  } catch {
    return null;
  }
}

export type MailTagColor = 'blue' | 'orange' | 'purple' | 'green' | 'pink' | 'red' | 'teal';

/** 색판은 닫혀 있다 — 임의 hex 를 안 받는다(정본 B12). 서버 enum 과 **같은 값·같은 순서**. */
export const MAIL_TAG_COLORS: MailTagColor[] = ['blue', 'orange', 'purple', 'green', 'pink', 'red', 'teal'];

/** 태그 하나. `color` 는 액센트 7 이름이다. */
export interface MailTag {
  id: string;
  name: string;
  color: MailTagColor;
  sortOrder: number;
}

/** 내 태그 — 옛 서버에는 길이 없다(404) → 빈 목록. 화면이 「아직 없습니다」로 그린다. */
export async function listMailTags(): Promise<MailTag[]> {
  try {
    const { data } = await apiClient.get<{ tags: MailTag[] }>('/mail/tags');
    return Array.isArray(data?.tags) ? data.tags : [];
  } catch {
    return [];
  }
}

export async function createMailTag(name: string, color: MailTagColor): Promise<MailTag> {
  const { data } = await apiClient.post<MailTag>('/mail/tags', { name, color });
  return data;
}

export async function updateMailTag(
  id: string, input: { name?: string; color?: MailTagColor },
): Promise<MailTag> {
  const { data } = await apiClient.patch<MailTag>(`/mail/tags/${id}`, input);
  return data;
}

export async function deleteMailTag(id: string): Promise<void> {
  await apiClient.delete(`/mail/tags/${id}`);
}

/* ══════════════════════════════════════════════════════════════════════════
   환경설정 > 스팸 — 수신허용목록 · 수신거부목록 (migration 160, 2026-09-05)
   ========================================================================== */

/**
 * 목록 한 줄.
 * ★ `source` 가 'SENDER' 면 상세에서 「이 사람 메일도 차단」을 눌러 만들어진 줄이다
 *   (`mail_blocked_senders`). 화면이 그 사실을 곁에 적는다 — 어디서 왔는지 모르면
 *   「내가 적은 적 없는 주소가 왜 여기 있나」가 된다.
 */
export interface MailAddressRule {
  id: string;
  address: string;
  source: 'ADDRESS' | 'SENDER';
}

export type MailAddressRuleKind = 'ALLOW' | 'BLOCK';

/** 두 목록 — 옛 서버에는 길이 없다(404) → 둘 다 빈 목록으로 그린다. */
export async function listMailAddressRules(): Promise<{ allow: MailAddressRule[]; block: MailAddressRule[] }> {
  try {
    const { data } = await apiClient.get<{ allow: MailAddressRule[]; block: MailAddressRule[] }>(
      '/mail/address-rules',
    );
    return {
      allow: Array.isArray(data?.allow) ? data.allow : [],
      block: Array.isArray(data?.block) ? data.block : [],
    };
  } catch {
    return { allow: [], block: [] };
  }
}

export async function createMailAddressRule(
  kind: MailAddressRuleKind, address: string,
): Promise<MailAddressRule> {
  const { data } = await apiClient.post<MailAddressRule>('/mail/address-rules', { kind, address });
  return data;
}

export async function deleteMailAddressRule(id: string): Promise<void> {
  await apiClient.delete(`/mail/address-rules/${encodeURIComponent(id)}`);
}

/** 전체삭제 — 화면에 보이는 것을 다 지운다(거부목록이면 사람 차단까지). */
export async function clearMailAddressRules(kind: MailAddressRuleKind): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>('/mail/address-rules', { params: { kind } });
  return data?.deleted ?? 0;
}

/* ══════════════════════════════════════════════════════════════════════════
   환경설정 > 자동분류 — 규칙 (migration 161, 2026-09-05)
   ========================================================================== */

export type MailFilterAddressKind = 'FROM' | 'TO';
export type MailFilterMatchMode = 'ALL' | 'ANY';

/**
 * 규칙 한 줄.
 * ★ 메일함·태그는 **이름까지** 온다 — id 만 오면 화면이 목록을 따로 받아 짝지어야 하고,
 *   한쪽이 늦게 오면 빈 칸이 보인다.
 * ★ `folder`·`tag` 가 null 인데 규칙은 남아 있을 수 있다 — 메일함·태그를 지우면 그렇게 된다.
 *   그때 화면은 「지워졌습니다」라고 적는다(규칙까지 사라지지는 않는다).
 */
export interface MailFilterRule {
  id: string;
  name: string;
  addressKind: MailFilterAddressKind;
  addresses: string[];
  subjectContains: string | null;
  matchMode: MailFilterMatchMode;
  sortOrder: number;
  folder: { id: string; name: string } | null;
  tag: { id: string; name: string; color: MailTagColor } | null;
}

export interface MailFilterRuleInput {
  name: string;
  addressKind: MailFilterAddressKind;
  addresses: string[];
  subjectContains: string | null;
  matchMode: MailFilterMatchMode;
  folderId: string | null;
  tagId: string | null;
}

/** 내 규칙(순서대로) — 옛 서버에는 길이 없다(404) → 빈 목록. */
export async function listMailFilterRules(): Promise<MailFilterRule[]> {
  try {
    const { data } = await apiClient.get<{ rules: MailFilterRule[] }>('/mail/filter-rules');
    return Array.isArray(data?.rules) ? data.rules : [];
  } catch {
    return [];
  }
}

export async function createMailFilterRule(input: MailFilterRuleInput): Promise<MailFilterRule> {
  const { data } = await apiClient.post<MailFilterRule>('/mail/filter-rules', input);
  return data;
}

export async function updateMailFilterRule(
  id: string, input: Partial<MailFilterRuleInput>,
): Promise<MailFilterRule> {
  const { data } = await apiClient.patch<MailFilterRule>(`/mail/filter-rules/${id}`, input);
  return data;
}

export async function deleteMailFilterRule(id: string): Promise<void> {
  await apiClient.delete(`/mail/filter-rules/${id}`);
}

export async function clearMailFilterRules(): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>('/mail/filter-rules');
  return data?.deleted ?? 0;
}

/** 순서 바꾸기 — 화면이 **줄 전체의 차례**를 보낸다(절반만 옮긴 순서가 제일 나쁘다). */
export async function reorderMailFilterRules(ids: string[]): Promise<MailFilterRule[]> {
  const { data } = await apiClient.patch<{ rules: MailFilterRule[] }>('/mail/filter-rules/order', { ids });
  return Array.isArray(data?.rules) ? data.rules : [];
}

/* ══════════════════════════════════════════════════════════════════════════
   환경설정 > 자동전달 (migration 162, 2026-09-05)
   ========================================================================== */

export type MailForwardMatchKind = 'ADDRESS' | 'DOMAIN';

/** 기본 전달 주소 한 줄. */
export interface MailForwardAddress { id: string; address: string }

/** 예외 규칙 — 보낸사람이 맞으면 **기본 주소 대신** 이 주소들로 간다. */
export interface MailForwardRule {
  id: string;
  matchKind: MailForwardMatchKind;
  /** 도메인이면 `@` 없이 온다. */
  matchValue: string;
  addresses: string[];
}

export interface MailForwardState {
  addresses: MailForwardAddress[];
  rules: MailForwardRule[];
  /**
   * 바깥 주소로 보낼 수 있나 — 「외부 메일 계정」의 보내기(SMTP)가 있어야 한다.
   * ★ 화면이 이것을 **미리** 적는다. 없으면 사내 주소로만 전달된다.
   */
  canSendExternal: boolean;
}

/** 옛 서버에는 길이 없다(404) → 빈 상태. */
export async function getMailForward(): Promise<MailForwardState> {
  try {
    const { data } = await apiClient.get<MailForwardState>('/mail/forward');
    return {
      addresses: Array.isArray(data?.addresses) ? data.addresses : [],
      rules: Array.isArray(data?.rules) ? data.rules : [],
      canSendExternal: data?.canSendExternal === true,
    };
  } catch {
    return { addresses: [], rules: [], canSendExternal: false };
  }
}

export async function addMailForwardAddress(address: string): Promise<MailForwardAddress> {
  const { data } = await apiClient.post<MailForwardAddress>('/mail/forward/addresses', { address });
  return data;
}

export async function deleteMailForwardAddress(id: string): Promise<void> {
  await apiClient.delete(`/mail/forward/addresses/${id}`);
}

export async function clearMailForwardAddresses(): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>('/mail/forward/addresses');
  return data?.deleted ?? 0;
}

export interface MailForwardRuleInput {
  matchKind: MailForwardMatchKind;
  matchValue: string;
  addresses: string[];
}

export async function createMailForwardRule(input: MailForwardRuleInput): Promise<MailForwardRule> {
  const { data } = await apiClient.post<MailForwardRule>('/mail/forward/rules', input);
  return data;
}

export async function updateMailForwardRule(
  id: string, input: Partial<MailForwardRuleInput>,
): Promise<MailForwardRule> {
  const { data } = await apiClient.patch<MailForwardRule>(`/mail/forward/rules/${id}`, input);
  return data;
}

export async function deleteMailForwardRule(id: string): Promise<void> {
  await apiClient.delete(`/mail/forward/rules/${id}`);
}

export async function clearMailForwardRules(): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>('/mail/forward/rules');
  return data?.deleted ?? 0;
}

/* ══════════════════════════════════════════════════════════════════════════
   환경설정 > 부재중응답 (migration 163, 2026-09-05)
   ========================================================================== */

export type MailVacationScope = 'ALL' | 'LISTED';

export interface MailVacationTarget { id: string; address: string }

export interface MailVacationState {
  enabled: boolean;
  /** `YYYY-MM-DD` — 없으면 기간이 안 정해진 것이다. */
  from: string | null;
  to: string | null;
  subject: string | null;
  body: string | null;
  scope: MailVacationScope;
  targets: MailVacationTarget[];
  /** 바깥 사람에게 답하려면 「외부 메일 계정」의 보내기(SMTP)가 있어야 한다. */
  canSendExternal: boolean;
}

const VACATION_EMPTY: MailVacationState = {
  enabled: false, from: null, to: null, subject: null, body: null,
  scope: 'ALL', targets: [], canSendExternal: false,
};

/** 옛 서버에는 길이 없다(404) → 빈 상태. */
export async function getMailVacation(): Promise<MailVacationState> {
  try {
    const { data } = await apiClient.get<MailVacationState>('/mail/vacation');
    return { ...VACATION_EMPTY, ...data, targets: Array.isArray(data?.targets) ? data.targets : [] };
  } catch {
    return VACATION_EMPTY;
  }
}

export interface MailVacationInput {
  enabled?: boolean;
  from?: string | null;
  to?: string | null;
  subject?: string | null;
  body?: string | null;
  scope?: MailVacationScope;
}

/** ★ 쓰는 길은 이것 하나다 — 칸들이 서로 얽혀 있어 서버가 함께 본다. */
export async function patchMailVacation(input: MailVacationInput): Promise<void> {
  await apiClient.patch('/mail/vacation', input);
}

export async function addMailVacationTarget(address: string): Promise<MailVacationTarget> {
  const { data } = await apiClient.post<MailVacationTarget>('/mail/vacation/targets', { address });
  return data;
}

export async function deleteMailVacationTarget(id: string): Promise<void> {
  await apiClient.delete(`/mail/vacation/targets/${id}`);
}

export async function clearMailVacationTargets(): Promise<number> {
  const { data } = await apiClient.delete<{ deleted: number }>('/mail/vacation/targets');
  return data?.deleted ?? 0;
}

/* ══════════════════════════════════════════════════════════════════════════
   환경설정 > 최근보낸메일 (migration 165, 2026-09-06)
   ========================================================================== */

/**
 * 내가 최근에 보낸 주소 한 줄.
 * ★ 저장된 표가 아니라 **보낸 메일에서 뽑아 낸 것**이다 — 그래서 「삭제」는 메일을 지우는 것이
 *   아니라 「앞으로 안 보이게 한다」는 뜻이고, 수신자 자동완성에서도 함께 빠진다.
 */
export interface MailRecentSent {
  address: string;
  name: string | null;
  uses: number;
  lastAt: string | null;
}

/** 옛 서버에는 길이 없다(404) → 빈 목록. */
export async function listMailRecentSent(): Promise<MailRecentSent[]> {
  try {
    const { data } = await apiClient.get<{ addresses: MailRecentSent[] }>('/mail/recent-sent');
    return Array.isArray(data?.addresses) ? data.addresses : [];
  } catch {
    return [];
  }
}

/** 한 줄 지우기 — 주소를 안 주면 **지금 목록에 있는 것을 전부** 감춘다. */
export async function hideMailRecentSent(address?: string): Promise<number> {
  const { data } = await apiClient.delete<{ hidden: number }>('/mail/recent-sent', {
    ...(address ? { params: { address } } : {}),
  });
  return data?.hidden ?? 0;
}
